import { FormEvent, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { errorStatus } from "../api/account";
import PinInput from "../components/Account/PinInput";
import "./Account.css";
import "./ResetPin.css";

const PIN_LENGTH = 5;
const LOGO_URL = "https://plnotimages.z29.web.core.windows.net/pliconround.png";

/** Mirrors Guid.TryParse on the service side (8-4-4-4-12 form). */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Landing page for a PIN-reset link emailed from the app, e.g.
 * https://pl.eeighttech.com/account/resetpin/{guid}?uid={token}
 *
 * Anonymous like the invoice route: no sign-in, no site chrome. The guid is
 * the only credential; the uid query token identifies the owner. The page
 * validates the link and either shows the change-PIN card or explains why the
 * link is dead.
 */
export default function ResetPin() {
	const { guid } = useParams<{ guid: string }>();
	const [searchParams] = useSearchParams();
	const uid = searchParams.get("uid") ?? "";

	const [state, setState] = useState<LinkState>({ kind: "checking" });

	useEffect(() => {
		// No point calling out — the service would 404 on these anyway.
		if (!guid || !GUID_RE.test(guid) || !uid) {
			setState({ kind: "invalid" });
			return;
		}

		let cancelled = false;
		api.account
			.validateResetPin(guid, uid)
			.then((status) => {
				if (cancelled) return;
				setState(status.Valid ? { kind: "valid" } : { kind: "expired" });
			})
			.catch((err) => {
				if (cancelled) return;
				setState(
					errorStatus(err) === 404 ? { kind: "invalid" } : { kind: "error" }
				);
			});

		return () => {
			cancelled = true;
		};
	}, [guid, uid]);

	return (
		<div className="account-route resetpin-route">
			<div className="account-page account-page--centered">
				<div className="signin-card">
					<div className="signin-brand">
						<img src={LOGO_URL} alt="Pocket Ledger" />
					</div>

					{state.kind === "checking" && (
						<>
							<h1 className="signin-title">Checking your link…</h1>
							<p className="signin-subtitle">
								Please wait a moment while we verify your reset link.
							</p>
						</>
					)}

					{state.kind === "valid" && guid && uid && (
						<>
							<h1 className="signin-title">Reset your PIN</h1>
							<p className="signin-subtitle">
								Choose a new 5-digit PIN. You'll use it to sign in to Pocket
								Ledger from now on.
							</p>
							<ChangePinForm guid={guid} uid={uid} />
						</>
					)}

					{state.kind === "expired" && (
						<ResetPinMessage
							icon="!"
							iconClass="resetpin-message-icon--error"
							title="This link has expired"
							text="Reset links are only valid for 24 hours. Request a new one from the Pocket Ledger app (Forgot PIN?), then open the fresh link."
						/>
					)}

					{state.kind === "invalid" && (
						<ResetPinMessage
							icon="?"
							iconClass="resetpin-message-icon--error"
							title="This link is invalid"
							text="It may have already been used, or the link was copied incorrectly. Request a new one from the Pocket Ledger app (Forgot PIN?)."
						/>
					)}

					{state.kind === "error" && (
						<ResetPinMessage
							icon="!"
							iconClass="resetpin-message-icon--error"
							title="Couldn't check your link"
							text="Something went wrong on our side. Please try again in a moment."
						>
							<button
								className="resetpin-btn resetpin-btn-secondary"
								onClick={() => window.location.reload()}
							>
								Try again
							</button>
						</ResetPinMessage>
					)}
				</div>
			</div>
		</div>
	);
}

type LinkState =
	| { kind: "checking" }
	| { kind: "valid" }
	| { kind: "expired" }
	| { kind: "invalid" }
	| { kind: "error" };

// ── Change-PIN form ─────────────────────────────────────────────────────────

function ChangePinForm({ guid, uid }: { guid: string; uid: string }) {
	const [newPin, setNewPin] = useState("");
	const [confirmPin, setConfirmPin] = useState("");
	const [error, setError] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	const ready = newPin.length === PIN_LENGTH && confirmPin.length === PIN_LENGTH;

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!ready || submitting) return;

		if (newPin !== confirmPin) {
			setError("The two PINs don't match. Please try again.");
			setConfirmPin("");
			return;
		}

		setError("");
		setSubmitting(true);

		try {
			await api.account.setResetPin(guid, uid, newPin);
			setDone(true);
		} catch {
			setError("Couldn't update your PIN right now. Please try again.");
			setConfirmPin("");
		}

		setSubmitting(false);
	};

	if (done) {
		return (
			<ResetPinMessage
				icon="✓"
				iconClass="resetpin-message-icon--ok"
				title="PIN updated"
				text="Your PIN has been changed. Sign in to Pocket Ledger with your new PIN."
			/>
		);
	}

	return (
		<form className="signin-form" onSubmit={handleSubmit}>
			<div className="signin-field">
				<label>New PIN</label>
				<PinInput
					value={newPin}
					onChange={setNewPin}
					length={PIN_LENGTH}
					disabled={submitting}
				/>
			</div>

			<div className="signin-field">
				<label>Confirm new PIN</label>
				<PinInput
					value={confirmPin}
					onChange={setConfirmPin}
					length={PIN_LENGTH}
					disabled={submitting}
				/>
			</div>

			{error && <div className="signin-error">{error}</div>}

			<button className="signin-btn" type="submit" disabled={!ready || submitting}>
				{submitting ? "Updating…" : "Update PIN"}
			</button>
		</form>
	);
}

// ── Message card (expired / invalid / error / done) ─────────────────────────

type ResetPinMessageProps = {
	icon: string;
	iconClass: string;
	title: string;
	text: string;
	children?: React.ReactNode;
};

function ResetPinMessage({
	icon,
	iconClass,
	title,
	text,
	children,
}: ResetPinMessageProps) {
	return (
		<div className="resetpin-message">
			<div className={`resetpin-message-icon ${iconClass}`}>{icon}</div>
			<h1 className="resetpin-message-title">{title}</h1>
			<p className="resetpin-message-text">{text}</p>
			{children ?? (
				<LinkButton />
			)}
		</div>
	);
}

function LinkButton() {
	return (
		<Link className="resetpin-btn resetpin-btn-secondary" to="/account">
			Go to sign in
		</Link>
	);
}
